import clsx from 'clsx';
import type { FunctionComponent } from 'react';
import { useCallback, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { IconChatBubble7, IconCrossSmall, IconDashboard } from '~/ui/core/icons';

import backgroundImage from '../horizon-1-3/background-2.webp';
import type { AnnouncementContentProps } from '../types';
import agentCoworkerImage from './agent-coworker.webp';
import agentUseCasesImage from './agent-use-cases.webp';
import architectureOverviewImage from './architecture-overview.webp';
import reviewAssignmentImage from './review-assignment.webp';

type Step = {
    readonly descriptionKey: TranslationKey;
    readonly headlineKey: TranslationKey;
    readonly hint?: StepHint;
    readonly image: string;
    readonly titleKey: TranslationKey;
};

type StepHint = {
    readonly icon: 'agent' | 'dashboard';
    readonly route?: string;
    readonly textKey: TranslationKey;
};

const STEPS: readonly Step[] = [
    {
        descriptionKey: 'announcements.horizon_1_4_agent_dashboard.architecture_overview.description',
        headlineKey: 'announcements.horizon_1_4_agent_dashboard.architecture_overview.headline',
        hint: {
            icon: 'dashboard',
            route: '/dashboard-v2',
            textKey: 'announcements.horizon_1_4_agent_dashboard.architecture_overview.hint',
        },
        image: architectureOverviewImage,
        titleKey: 'announcements.horizon_1_4_agent_dashboard.architecture_overview.title',
    },
    {
        descriptionKey: 'announcements.horizon_1_4_agent_dashboard.dashboard_workstreams.description',
        headlineKey: 'announcements.horizon_1_4_agent_dashboard.dashboard_workstreams.headline',
        image: reviewAssignmentImage,
        titleKey: 'announcements.horizon_1_4_agent_dashboard.dashboard_workstreams.title',
    },
    {
        descriptionKey: 'announcements.horizon_1_4_agent_dashboard.agent_context.description',
        headlineKey: 'announcements.horizon_1_4_agent_dashboard.agent_context.headline',
        hint: {
            icon: 'agent',
            textKey: 'announcements.horizon_1_4_agent_dashboard.agent_context.hint',
        },
        image: agentCoworkerImage,
        titleKey: 'announcements.horizon_1_4_agent_dashboard.agent_context.title',
    },
    {
        descriptionKey: 'announcements.horizon_1_4_agent_dashboard.agent_activity.description',
        headlineKey: 'announcements.horizon_1_4_agent_dashboard.agent_activity.headline',
        image: agentUseCasesImage,
        titleKey: 'announcements.horizon_1_4_agent_dashboard.agent_activity.title',
    },
];

type StepDotProps = {
    readonly index: number;
    readonly isActive: boolean;
    readonly onClick: () => void;
};

const StepDot: FunctionComponent<StepDotProps> = ({ index, isActive, onClick }) => (
    <button
        aria-label={`Go to slide ${index + 1}`}
        className="group relative flex h-8 w-4 items-center justify-center border-none bg-transparent p-0 cursor-pointer"
        type="button"
        onClick={onClick}
    >
        <span
            className={clsx(
                'block h-2 w-2 rounded-full transition-[background-color,scale] duration-200 group-hover:bg-border-strong',
                isActive ? 'scale-100 bg-text-heading' : 'scale-90 bg-border-subtle',
            )}
        />
    </button>
);

type StepHintIconProps = {
    readonly icon: StepHint['icon'];
};

const StepHintIcon: FunctionComponent<StepHintIconProps> = ({ icon }) => {
    const iconClassName = 'size-3 shrink-0 text-text-content-subtle';

    return icon === 'dashboard' ? (
        <IconDashboard aria-hidden={true} className={iconClassName} />
    ) : (
        <IconChatBubble7 aria-hidden={true} className={iconClassName} />
    );
};

type StepHintBadgeProps = {
    readonly hint: StepHint;
    readonly onNavigate: (route: string) => void;
};

const StepHintBadge: FunctionComponent<StepHintBadgeProps> = ({ hint, onNavigate }) => {
    const translate = useTranslate();
    const handleClick = useCallback(() => {
        if (hint.route !== undefined) {
            onNavigate(hint.route);
        }
    }, [hint.route, onNavigate]);

    return (
        <div className="mt-m">
            <Badge variant="gray" onClick={hint.route === undefined ? undefined : handleClick}>
                <StepHintIcon icon={hint.icon} />
                {translate(hint.textKey)}
            </Badge>
        </div>
    );
};

export const Horizon14AgentDashboardContent: FunctionComponent<AnnouncementContentProps> = ({
    onDismiss,
    onNavigate,
}) => {
    const translate = useTranslate();
    const [step, setStep] = useState(0);

    const handleBack = useCallback(() => setStep((s) => s - 1), []);
    const handleNext = useCallback(() => setStep((s) => s + 1), []);
    const activeStep = STEPS[step];

    if (activeStep === undefined) {
        return null;
    }

    return (
        <div className="flex h-[610px] flex-col bg-surface-raised p-xxl">
            <div className="relative flex h-[380px] shrink-0 items-center justify-center overflow-hidden rounded-lg bg-surface-hover px-4xl py-l">
                <img
                    alt=""
                    aria-hidden={true}
                    className="absolute inset-0 h-full w-full object-cover object-[center_65%]"
                    src={backgroundImage}
                />
                <div className="absolute right-l top-l z-20">
                    <Button
                        aria-label={translate('common.close')}
                        className="!text-white-200 hover:!text-gray-800 [&>svg]:!text-white-200 hover:[&>svg]:!text-gray-800"
                        iconOnly={true}
                        size="sm"
                        variant="tertiary"
                        onClick={onDismiss}
                    >
                        <IconCrossSmall />
                    </Button>
                </div>
                <div className="relative z-10 mx-auto flex h-full w-full max-w-[390px] items-center justify-center">
                    <img
                        alt={translate(activeStep.titleKey)}
                        className="block max-h-[320px] max-w-full rounded-lg object-contain"
                        src={activeStep.image}
                    />
                </div>
            </div>

            <div className="flex flex-1 flex-col pt-xxl">
                <div className="flex h-[118px] flex-col items-start justify-start">
                    <h2
                        className="m-0 max-w-full text-balance text-h4 font-normal text-text-heading tracking-normal"
                        style={{
                            fontFeatureSettings: '"kern" on, "ss03" on',
                            fontKerning: 'normal',
                            textRendering: 'optimizeLegibility',
                        }}
                    >
                        {translate(activeStep.headlineKey)}
                    </h2>
                    <p className="m-0 mt-l max-w-full text-pretty text-body-s font-normal leading-relaxed text-text-muted">
                        {translate(activeStep.descriptionKey)}
                    </p>
                    {activeStep.hint === undefined ? null : (
                        <StepHintBadge hint={activeStep.hint} onNavigate={onNavigate} />
                    )}
                </div>

                <div className="mt-auto flex items-center justify-between gap-l">
                    <nav aria-label="Announcement slides" className="flex items-center">
                        {STEPS.map((s, i) => (
                            <StepDot key={s.titleKey} index={i} isActive={step === i} onClick={() => setStep(i)} />
                        ))}
                    </nav>

                    <div className="flex items-center justify-end gap-xs">
                        {step > 0 && (
                            <Button variant="tertiary" onClick={handleBack}>
                                {translate('announcements.back')}
                            </Button>
                        )}
                        {step < STEPS.length - 1 ? (
                            <Button onClick={handleNext}>{translate('announcements.continue')}</Button>
                        ) : (
                            <Button type="button" onClick={onDismiss}>
                                {translate('announcements.get_started')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ANNOUNCEMENT_CONTENT: Record<string, FunctionComponent<AnnouncementContentProps>> = {
    horizon_1_4_agent_dashboard: Horizon14AgentDashboardContent,
};
